import { createTheme } from '@mui/material';

// ─── Design tokens ──────────────────────────────────────────────────────────
// Canonical scales referenced by sx props across the app. Import these instead
// of writing raw numbers/strings so the scale stays a deliberate, finite set
// rather than drifting into one-off values per component.
//
// RADIUS: multiplies theme.shape.borderRadius (8px) via MUI's sx transform —
// `sx={{ borderRadius: RADIUS.md }}` renders at RADIUS.md * 8px.
export const RADIUS = {
  micro: 0.75,  // 6px    — tiny badges/pills (kbd shortcuts, inline tags)
  sm: 1,        // 8px    — tight controls (matches MuiButton/MuiTextField)
  md: 1.5,      // 12px   — standard cards, list rows (matches reference's rounded-xl)
  lg: 2,        // 16px   — panels, prominent surfaces (most common default)
  xl: 2.5,      // 20px   — large elevated surfaces
  xxl: 3,        // 24px  — hero/feature cards
};

// FONT_SIZE: anchored to theme.typography variants below, on a 1px/16px-root
// grid (0.625/0.688/0.75/0.813/0.875/0.938/1 = 10/11/12/13/14/15/16px).
export const FONT_SIZE = {
  micro: '0.625rem',   // 10px — counters, micro labels
  xs: '0.688rem',      // 11px — matches typography.overline
  sm: '0.75rem',       // 12px — matches typography.caption
  base: '0.813rem',    // 13px — matches typography.body1 / body2
  md: '0.875rem',      // 14px — matches typography.subtitle2
  lg: '0.938rem',       // 15px — matches typography.subtitle1
};

const getThemeOptions = (mode = 'light', customColors = {}) => {
  // Primary shifted toward the reference app's blue (#2563eb, Tailwind blue-600) —
  // crisper and more saturated than the old #1565c0 while staying recognizably "blue".
  const primaryColor = customColors.primaryColor || '#2563eb';
  const secondaryColor = customColors.secondaryColor || '#ff6b35';

  return {
    palette: {
      mode,
      primary: {
        main: primaryColor,
        light: mode === 'dark' ? '#60a5fa' : '#3b82f6',
        dark: '#1d4ed8',
        contrastText: '#ffffff',
      },
      secondary: {
        main: secondaryColor,
        light: '#ff9a6c',
        dark: '#c43e00',
        contrastText: '#ffffff',
      },
      success: {
        main: '#2e7d32',
        light: '#60ad5e',
        dark: '#005005',
      },
      warning: {
        main: '#ed6c02',
        light: '#ff9d3f',
        dark: '#b53d00',
      },
      error: {
        main: '#d32f2f',
        light: '#ef5350',
        dark: '#c62828',
      },
      info: {
        main: '#0288d1',
        light: '#03a9f4',
        dark: '#01579b',
      },
      // Semantic "AI / Agents" identity — used consistently across Admin agent
      // panels, the KoorAI Assistant widget, and marketing pages. Promoted from
      // hardcoded hex (#9c27b0/#7b1fa2) that appeared in 11 files independently.
      agentPurple: {
        main: '#9c27b0',
        light: '#ba68c8',
        dark: '#7b1fa2',
        contrastText: '#ffffff',
      },
      background: mode === 'dark'
        ? { default: '#0a0e14', paper: '#1a1f2e' }
        : { default: '#f5f7fa', paper: '#ffffff' },
      text: mode === 'dark'
        ? { primary: '#e4e8ef', secondary: '#8b95a5' }
        : { primary: '#1a2027', secondary: '#5f6b7a' },
      divider: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
      fontSize: 13,
      h1: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.02em' },
      h2: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.01em' },
      h3: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.3 },
      h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.35 },
      h5: { fontSize: '1.1rem', fontWeight: 600, lineHeight: 1.4 },
      h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
      subtitle1: { fontSize: '0.938rem', fontWeight: 500, lineHeight: 1.5 },
      subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5 },
      body1: { fontSize: '0.813rem', lineHeight: 1.6 },
      body2: { fontSize: '0.813rem', lineHeight: 1.6 },
      caption: { fontSize: '0.75rem', lineHeight: 1.5, letterSpacing: '0.02em' },
      overline: { fontSize: '0.688rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' },
      button: { textTransform: 'none', fontWeight: 500, fontSize: '0.875rem' },
    },
    shape: {
      // 8px base radius — matches the reference app's shadcn/ui --radius token.
      // RADIUS.* multipliers above (sm/md/lg/xl/xxl) scale off this value.
      borderRadius: 8,
    },
    shadows: [
      'none',
      '0 1px 2px rgba(0,0,0,0.04)',
      '0 1px 4px rgba(0,0,0,0.06)',
      '0 2px 8px rgba(0,0,0,0.06)',
      '0 4px 12px rgba(0,0,0,0.08)',
      '0 6px 16px rgba(0,0,0,0.08)',
      '0 8px 24px rgba(0,0,0,0.1)',
      '0 12px 32px rgba(0,0,0,0.1)',
      '0 16px 40px rgba(0,0,0,0.12)',
      '0 20px 48px rgba(0,0,0,0.12)',
      '0 24px 56px rgba(0,0,0,0.14)',
      ...Array(14).fill('0 24px 56px rgba(0,0,0,0.14)'),
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { width: 6, height: 6 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
              borderRadius: 3,
            },
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '6px 16px',
            fontWeight: 500,
            transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
          },
          contained: {
            '&:hover': {
              boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
            },
          },
          outlined: {
            borderWidth: '1.5px',
            '&:hover': { borderWidth: '1.5px' },
          },
          sizeSmall: { padding: '4px 12px', fontSize: '0.813rem' },
          sizeLarge: { padding: '10px 24px', fontSize: '0.938rem' },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: mode === 'dark'
                ? '0 8px 24px rgba(0,0,0,0.4)'
                : '0 8px 24px rgba(0,0,0,0.08)',
              borderColor: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
            },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small', variant: 'outlined' },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              transition: 'box-shadow 200ms',
              '&:hover': {
                boxShadow: '0 0 0 2px rgba(21, 101, 192, 0.08)',
              },
              '&.Mui-focused': {
                boxShadow: `0 0 0 3px ${mode === 'dark' ? 'rgba(71, 145, 219, 0.2)' : 'rgba(21, 101, 192, 0.12)'}`,
              },
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            // Flat, quiet header — no uppercase/background tint, matches the
            // reference app's restrained shadcn-style table (font-medium,
            // text-muted-foreground, single hairline border, no fill).
            '& .MuiTableCell-head': {
              fontWeight: 500,
              fontSize: '0.813rem',
              color: mode === 'dark' ? '#8b95a5' : '#5f6b7a',
              borderBottom: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
              padding: '10px 16px',
              whiteSpace: 'nowrap',
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            // Neutral gray hover (not brand-tinted) — quieter across dense tables.
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            },
            '&:last-child td': { borderBottom: 0 },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '10px 16px',
            fontSize: '0.813rem',
            borderColor: mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            height: 26,
            borderRadius: 6,
            fontWeight: 500,
            fontSize: '0.75rem',
          },
          sizeSmall: { height: 22, fontSize: '0.688rem' },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: '1.125rem',
            fontWeight: 600,
            padding: '20px 24px 12px',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            minHeight: 44,
            '&.Mui-selected': { fontWeight: 600 },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { height: 6, borderRadius: 3 },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 10 },
          standardWarning: {
            backgroundColor: mode === 'dark' ? 'rgba(237, 108, 2, 0.12)' : 'rgba(237, 108, 2, 0.08)',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 6,
            fontSize: '0.75rem',
            padding: '6px 12px',
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            fontWeight: 600,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '2px 8px',
            padding: '8px 12px',
            transition: 'all 150ms',
            '&.Mui-selected': {
              backgroundColor: `${primaryColor}14`,
              borderLeft: `3px solid ${primaryColor}`,
              '&:hover': {
                backgroundColor: `${primaryColor}1a`,
              },
            },
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: { fontSize: '0.813rem' },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            transition: 'all 150ms',
          },
        },
      },
      MuiSelect: {
        defaultProps: { size: 'small' },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: { borderRadius: 10 },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { borderRadius: 10, marginTop: 4 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            margin: '2px 6px',
            padding: '8px 12px',
            fontSize: '0.875rem',
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: { fontSize: '0.688rem', fontWeight: 600 },
        },
      },
    },
  };
};

export const createAppTheme = (mode = 'light', customColors = {}) => {
  return createTheme(getThemeOptions(mode, customColors));
};

export default getThemeOptions;
